import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { getDocument, RenderTask } from 'pdfjs-dist/webpack';
import { LabelPdfCropSelection, PdfRectCropRegion } from '../../services/productComparisonService';

type Props = {
  file: File;
  cropSelection?: LabelPdfCropSelection | null;
};

type ContainerSize = {
  width: number;
  height: number;
};

type RenderState = {
  width: number;
  height: number;
  pageNumber: number;
};

function getPreviewPageNumber(selection: LabelPdfCropSelection | null | undefined): number {
  return selection?.pageRegions?.[0]?.pageNumber || 1;
}

function getCropRegionForPage(
  selection: LabelPdfCropSelection | null | undefined,
  pageNumber: number,
): PdfRectCropRegion | null {
  return selection?.pageRegions?.find((region) => region.pageNumber === pageNumber) || null;
}

const PdfPreviewSurface: React.FC<Props> = ({ file, cropSelection }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const [containerSize, setContainerSize] = useState<ContainerSize>({ width: 0, height: 0 });
  const [renderState, setRenderState] = useState<RenderState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewPageNumber = useMemo(() => getPreviewPageNumber(cropSelection), [cropSelection]);
  const previewCropRegion = useMemo(
    () => getCropRegionForPage(cropSelection, previewPageNumber),
    [cropSelection, previewPageNumber],
  );

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const element = containerRef.current;
    const updateSize = () => {
      const nextWidth = Math.max(0, element.clientWidth - 16);
      const nextHeight = Math.max(0, element.clientHeight - 16);
      setContainerSize((prev) =>
        prev.width !== nextWidth || prev.height !== nextHeight
          ? { width: nextWidth, height: nextHeight }
          : prev);
    };

    updateSize();

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!canvasRef.current || containerSize.width <= 0 || containerSize.height <= 0) {
      return undefined;
    }

    let cancelled = false;
    let loadingTask: ReturnType<typeof getDocument> | null = null;

    const renderPreview = async () => {
      setLoading(true);
      setError(null);

      try {
        const fileBytes = new Uint8Array(await file.arrayBuffer());
        loadingTask = getDocument({ data: fileBytes });
        const pdf = await loadingTask.promise;
        if (cancelled) {
          await loadingTask.destroy();
          return;
        }

        const pageNumber = Math.min(Math.max(1, previewPageNumber), pdf.numPages);
        const page = await pdf.getPage(pageNumber);
        if (cancelled || !canvasRef.current) return;

        const baseViewport = page.getViewport({ scale: 1, rotation: 0 });
        const scale = Math.max(
          0.1,
          Math.min(
            containerSize.width / baseViewport.width,
            containerSize.height / baseViewport.height,
          ),
        );
        const viewport = page.getViewport({ scale, rotation: 0 });
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Canvas-kontekst saknas.');
        }

        renderTaskRef.current?.cancel();
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);

        const renderTask = page.render({
          canvasContext: context,
          viewport,
          canvas,
        });
        renderTaskRef.current = renderTask;
        await renderTask.promise;

        if (!cancelled) {
          setRenderState({
            width: viewport.width,
            height: viewport.height,
            pageNumber,
          });
        }
      } catch (renderError: any) {
        if (!cancelled && renderError?.name !== 'RenderingCancelledException') {
          setError(renderError?.message || 'Kunde inte rendera PDF-preview.');
          setRenderState(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    renderPreview();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      loadingTask?.destroy().catch(() => undefined);
    };
  }, [containerSize.height, containerSize.width, file, previewPageNumber]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F5F5F5',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: renderState ? `${renderState.width}px` : '1px',
          height: renderState ? `${renderState.height}px` : '1px',
          flex: '0 0 auto',
          visibility: renderState && !error ? 'visible' : 'hidden',
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          sx={{
            width: renderState ? `${renderState.width}px` : '1px',
            height: renderState ? `${renderState.height}px` : '1px',
            display: 'block',
          }}
        />
        {renderState && previewCropRegion && !error && (
          <>
            <Box
              sx={{
                position: 'absolute',
                left: `${previewCropRegion.xPct * 100}%`,
                top: `${previewCropRegion.yPct * 100}%`,
                width: `${previewCropRegion.widthPct * 100}%`,
                height: `${previewCropRegion.heightPct * 100}%`,
                border: '3px solid',
                borderColor: 'success.main',
                boxShadow: '0 0 0 9999px rgba(17, 24, 39, 0.18)',
                bgcolor: 'rgba(76, 175, 80, 0.08)',
                pointerEvents: 'none',
              }}
            />
            <Chip
              size="small"
              label={`Beskärning markerad${renderState.pageNumber > 1 ? ` (sida ${renderState.pageNumber})` : ''}`}
              sx={{
                position: 'absolute',
                right: 10,
                bottom: 10,
                bgcolor: 'rgba(46, 125, 50, 0.92)',
                color: '#fff',
              }}
            />
          </>
        )}
      </Box>

      {error && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            textAlign: 'center',
          }}
        >
          {error}
        </Typography>
      )}

      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.45)',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default PdfPreviewSurface;
